import type { UnitOfMeasure } from '../types/unit-of-measure'
import type { ParsedValue } from '../types/parsed-value'
import type { FormatOptions } from '../types/format-options'
import {
  BOUND_DISPLAY_DECIMALS,
  PT_PER_INCH,
  PT_PER_PICA,
} from '../constants/unit-conversion-limits'

/**
 * A raw points value as a plain decimal in `unit` (never the Picas "XpY" composite notation),
 * with its unit label — e.g. for stating a bound in a validation message. Trims trailing zeros
 * itself rather than via `formatNumber` — that helper caps decimals at 4 when not preserving
 * trailing zeros, which would round a tiny value (e.g. `MIN_VALUE_PT` in Picas) down to a
 * misleading "0".
 */
export function formatPlainValueInUnit(pointsValue: number, unit: UnitOfMeasure): string {
  const perUnit = unit === 'Inches' ? PT_PER_INCH : unit === 'Picas' ? PT_PER_PICA : 1
  const value = pointsValue / perUnit
  const formatted = Number.isInteger(value)
    ? String(value)
    : value.toFixed(BOUND_DISPLAY_DECIMALS).replace(/0+$/, '').replace(/\.$/, '')

  return `${formatted} ${getUnitLabel(unit)}`
}

/**
 * Convert points to target unit for display.
 * Adaptive precision: show only precision needed unless decimalPlaces/preserveTrailingZeros specified.
 */
export function formatPointsForDisplay(
  points: number,
  targetUnit: UnitOfMeasure,
  options?: FormatOptions
): string {
  const maxDecimals = options?.maxDecimals ?? 4
  const preserveTrailingZeros = options?.preserveTrailingZeros ?? false
  const decimalPlaces = options?.decimalPlaces

  switch (targetUnit) {
    case 'Points': {
      const rounded = roundToPrecision(points, maxDecimals)

      return formatNumber(rounded, maxDecimals, preserveTrailingZeros, decimalPlaces)
    }

    case 'Inches': {
      const inches = points / PT_PER_INCH
      const rounded = roundToPrecision(inches, maxDecimals)

      return formatNumber(rounded, maxDecimals, preserveTrailingZeros, decimalPlaces)
    }

    case 'Picas': {
      return formatPicas(points, maxDecimals, preserveTrailingZeros, decimalPlaces)
    }

    default:
      return formatNumber(points, maxDecimals, preserveTrailingZeros, decimalPlaces)
  }
}

/**
 * Format value in picas as XpY. When |totalPicas| < 1, show 0pY (e.g. 0p.125) instead of decimal
 * picas. Decomposes the *magnitude* into whole picas + remainder points and reattaches the sign
 * as a single leading "-" (e.g. -10p9, mirroring how it was typed) — decomposing the signed value
 * directly would need `Math.floor` to round a negative number toward zero, which it never does
 * (`Math.floor(-10.75)` is -11, not -10), silently shifting the whole-picas part one further
 * negative and forcing a compensating positive remainder (e.g. -10p9 redisplaying as -11p3).
 */
export function formatPicas(
  points: number,
  maxDecimals: number,
  preserveTrailingZeros: boolean,
  decimalPlaces?: number
): string {
  const totalPicas = points / PT_PER_PICA
  const isNegative = totalPicas < 0
  const signPrefix = isNegative ? '-' : ''
  const absTotalPicas = Math.abs(totalPicas)

  // When 0 < |totalPicas| < 1: show ±0pY (e.g. 0p.125, -0p.125) not 0.0104p0
  if (absTotalPicas > 0 && absTotalPicas < 1) {
    const remainderPoints = Math.abs(points)
    const fracStr = formatNumber(remainderPoints, maxDecimals, preserveTrailingZeros, decimalPlaces)

    return `${signPrefix}0p${fracStr}`
  }

  const wholePicas = Math.floor(absTotalPicas)
  const remainderPoints = (absTotalPicas - wholePicas) * PT_PER_PICA
  const fracPoints = roundToPrecision(remainderPoints, maxDecimals)

  if (fracPoints < 0.0001) {
    if (decimalPlaces !== undefined || preserveTrailingZeros) {
      return `${signPrefix}${wholePicas}p${formatNumber(
        0,
        maxDecimals,
        preserveTrailingZeros,
        decimalPlaces
      )}`
    }

    return `${signPrefix}${wholePicas}p0`
  }

  const fracStr = formatNumber(fracPoints, maxDecimals, preserveTrailingZeros, decimalPlaces)

  return `${signPrefix}${wholePicas}p${fracStr}`
}

export function roundToPrecision(value: number, maxDecimals: number): number {
  if (maxDecimals <= 0) return Math.round(value)
  const factor = 10 ** maxDecimals

  return Math.round(value * factor) / factor
}

export function formatNumber(
  value: number,
  maxDecimals: number,
  preserveTrailingZeros: boolean,
  decimalPlaces?: number
): string {
  if (Number.isInteger(value) && decimalPlaces === undefined) return String(value)

  const decimals =
    decimalPlaces !== undefined
      ? decimalPlaces
      : preserveTrailingZeros
        ? maxDecimals
        : Math.min(maxDecimals, 4)
  const str = value.toFixed(decimals)

  if (!preserveTrailingZeros && decimalPlaces === undefined) {
    return str.replace(/\.?0+$/, '')
  }

  return str
}

/**
 * Get the unit label for display (e.g. "pc" for picas, "pt" for points, "in" for inches).
 */
export function getUnitLabel(unit: UnitOfMeasure): string {
  switch (unit) {
    case 'Picas':
      return 'pc'
    case 'Points':
      return 'pt'
    case 'Inches':
      return 'in'
    default:
      return 'pc'
  }
}

/**
 * Check if the parsed input is in the same unit as target (no conversion needed).
 * Used when user enters e.g. "96in" and target is Inches - show as entered.
 */
export function isSameUnitAsTarget(parsed: ParsedValue, targetUnit: UnitOfMeasure): boolean {
  if (!parsed.hadUnit) return false
  switch (targetUnit) {
    case 'Inches':
      return parsed.inputUnit === 'inch'
    case 'Points':
      return parsed.inputUnit === 'point'
    case 'Picas':
      return parsed.inputUnit === 'pica' || parsed.inputUnit === 'picaPoint'
    default:
      return false
  }
}
